import {boundMethod} from "autobind-decorator";
import {parseISO} from "date-fns";
import {motion} from "framer-motion";
import React from "react";
import {injectIntl, IntlShape} from "react-intl";
import {Link, RouteComponentProps} from "react-router-dom";

import {animation, onClosed, onOpen} from "@toolbox/design/models";
import {ITemplateModel} from "./models";

import Time from "@toolbox/design/Time";
import ContainerActivity from "@toolbox/display-blocks/ContainerActivity";
import ContainerCol from "@toolbox/display-blocks/ContainerCol";
import Highlighter from "@toolbox/display-blocks/Highlighter";
import ModuleIcon, {getModuleInfo} from "@toolbox/display-blocks/ModuleIcon";
import T from "@translate/T";
import TemplateAudit from "./TemplateAudit";
import TemplateSearch from "./TemplateSearch";
import TemplateTaskPanel from "./TemplateTaskPanel";

interface ITemplateListProps extends RouteComponentProps {
    intl: IntlShape;
    baseUrl: string;
}

interface ITemplateListState {
    templates: ITemplateModel[];
    query: string;
}

class TemplateList extends React.PureComponent<
    ITemplateListProps,
    ITemplateListState
> {
    public readonly state: ITemplateListState = {templates: [], query: ""};

    @boundMethod
    public onChange(templates: ITemplateModel[], query: string) {
        this.setState({templates, query});
    }

    public render() {
        return (
            <ContainerCol>
                <ContainerActivity
                    left={this.renderLeft()}
                    right={this.renderRight()}
                />
            </ContainerCol>
        );
    }

    private renderLeft() {
        const {baseUrl, history, location, match} = this.props;

        return (
            <React.Fragment>
                <TemplateTaskPanel baseUrl={baseUrl} />

                <h3 id="analysis-templates-page">
                    <T>Analysis templates</T>
                </h3>
                <hr />

                <TemplateSearch
                    history={history}
                    location={location}
                    match={match}
                    onChange={this.onChange}
                >
                    {this.renderTemplates()}
                </TemplateSearch>
            </React.Fragment>
        );
    }

    private renderRight() {
        return <TemplateAudit baseUrl={this.props.baseUrl} />;
    }

    @boundMethod
    private renderTemplates() {
        const {baseUrl, intl} = this.props;
        const {templates, query} = this.state;

        return templates.map((template) => {
            const info = getModuleInfo(template.engine);

            return (
                <motion.tr
                    key={template.id}
                    animate={onOpen}
                    exit={onClosed}
                    initial={onClosed}
                    transition={animation}
                >
                    <td>
                        <Link
                            id={"templates-link-" + template.id}
                            title={info.type(intl)}
                            to={`${baseUrl}/edit/${template.id}`}
                        >
                            <ModuleIcon
                                icon={info.icon}
                                name={template.name}
                                highlighter={query}
                            />
                        </Link>
                    </td>
                    <td id={"templates-engine-" + template.id}>
                        <Highlighter query={query} text={info.type(intl)} />
                    </td>
                    <td id={"templates-modified-" + template.id}>
                        <Time value={parseISO(template.modified)} />
                    </td>
                </motion.tr>
            );
        });
    }
}

export default injectIntl(TemplateList);
